import type { BrandConfig } from "./types";

export const fitupBrand: BrandConfig = {
  name: "fitup",
  siteTitle: "FitUP Member",

  // Primitive color tokens reused across multiple features.
  colors: {
    // Global neutrals and transparent keyword.
    common: {
      white: "#FFFFFF",
      black: "#000000",
      transparent: "transparent",
    },

    // Brand identity, gradients, and accent fills.
    brand: {
      secondary: "#625B71",
      gradientStart: "#2AACE2",
      gradientMiddle: "#91288D",
      gradientAccent: "#E72B45",
      gradientWarm: "#F18230",
      gradientEnd: "#FFC82C",
      accentBronze: "#DE2826",
      accentCritical: "#FF0000",
      accentCriticalStrong: "#FF0011",
      creditsAccent: "#FFA300",
      creditsIcon: "#FFA402",
      creditsGradientStart: "#FED467",
      creditsGradientMid: "#FFDA3E",
      creditsGradientEnd: "#FFF8C7",
    },

    // Generic background surfaces and feedback fills.
    background: {
      appSecondary: "#222221",
      pagePrimary: "#FFFFFF",
      surfaceSubtle: "#FCFCFC",
      fieldPrimary: "#FFF9F2",
      feedbackSuccess: "#F5FEE9",
      feedbackWarning: "#FDF5E8",
      feedbackNeutral: "#F1F1F1",
      feedbackFreeze: "#E3F2FD",
    },

    // Generic text primitives.
    text: {
      onDarkPlaceholder: "#8E8E8E",
      onLightSecondary: "rgba(0, 0, 0, 0.6)",
      primary: "#1F1F1F",
      primaryAlt: "#1C1B1C",
      primaryStrong: "#181817",
      tertiary: "#8E8A86",
      tertiaryAlt: "#8E8E93",
      disabled: "#797677",
      emphasis: "rgba(0, 0, 0, 0.87)",
      disabledOnLight: "rgba(0, 0, 0, 0.38)",
    },

    // Shared border and outline colors.
    border: {
      secondary: "#CFCFCA",
      divider: "#EEEEEE",
      strong: "#8E8E8E",
      muted: "#C6C6C6",
      neutral: "#BDBDBD",
      iconSurface: "#E0E0E0",
    },

    // Semantic status colors.
    state: {
      error: "#BA1A1A",
      success: "#2E7D32",
      successBright: "#33B15B",
      warning: "#F18230",
      warningAlt: "#ED6C02",
      neutral: "#9E9E9E",
      freeze: "#2AACE2",
    },

    // Overlay colors for disabled, video, and translucent UI states.
    overlay: {
      disabled: "rgba(0, 0, 0, 0.12)",
      video: "rgba(0,0,0,0.35)",
      videoEnd: "rgba(0,0,0,0.45)",
      onDarkSoft: "rgba(255, 255, 255, 0.1)",
      onDarkDisabled: "#0000001F",
      mutedLight: "rgba(198, 198, 198, 0.12)",
    },

    // Shared icon fills.
    icon: {
      badgeBackground: "#EEE1CA",
      badgeForeground: "#484647",
      poolForeground: "#1C1B1C",
      targetForeground: "#C7C6C6",
    },
  },

  // Non-trivial gradient strings.
  gradients: {
    videoOverlay:
      "linear-gradient(180deg, rgba(0, 0, 0, 0.25) 0%, rgba(0, 0, 0, 0.45) 100%)",
  },

  // Authentication page composition.
  auth: {
    loginHeader: {
      title: "Login",
      showDivider: true,
    },
    footer: {
      logo: "fitup",
      text: "Powered by FitUP",
    },
  },

  // Member area screen-specific tokens.
  member: {
    secondaryCardBackground: "#FFFFFF",
    headerBackground: "#DE2826",
    headerPrimaryText: "#FFFFFF",
    headerSecondaryText: "#FCE9E9",
    sectionTitleColor: "#1C1B1C",
    sectionDividerColor: "#E0E0E0",
    emptyStateText: "#8E8E8E",
    creditsPage: {
      headerBackground: "#C90C14",
      headerText: "#FFFFFF",
      filterIcon: "#B8B8B8",
      monthText: "#1C1B1C",
      transactionDate: "#B8B8B8",
      transactionDivider: "#D9D9D9",
    },
    qrSection: {
      cardBackground: "#FFFFFF",
      iconBackground: "#DE2826",
      iconColor: "#FFFFFF",
      labelColor: "#1C1B1C",
    },
    goalSetting: {
      dateActiveBackground: "#FCE9E9",
      dateActiveBorder: "#FCE9E9",
      dateActiveText: "#DE2826",
      dateInactiveBackground: "#FFFFFF",
      dateInactiveBorder: "#EEEEEE",
      dateInactiveText: "#B8B8B8",
      cardBackground: "#FFFFFF",
      cardTitle: "#1C1B1C",
      cardText: "#1C1B1C",
      cardMutedText: "#B8B8B8",
      cardDivider: "#EEEEEE",
      cardActionText: "#B8B8B8",
      iconBackground: "#FCE9E9",
      iconForeground: "#DE2826",
      targetIcon: "#B8B8B8",
      chartLine: "#D9D9D9",
      chartLabel: "#D9D9D9",
      chartGrid: "#EAEAEA",
      sectionAccent: "#DE2826",
    },
  },

  booking: {
    timeSlot: {
      titleColor: "#1C1B1C",
      borderColor: "#EEEEEE",
      textColor: "#8E8E8E",
      selectedBackground: "#FCE9E9",
      selectedTextColor: "#DE2826",
      hoverBackground: "rgba(198, 198, 198, 0.12)",
    },
  },

  facility: {
    booking: {
      paymentSlip: {
        titleColor: "#DE2826",
      },
    },
  },

  // Consumer-facing palette consumed by COLORS, theme, and feature styles.
  palette: {
    // Core brand and shell tokens.
    primary: "#DE2826",
    primaryLight: "#FCE9E9",
    bodyBackground: "#FAFAFA",
    gradient: "#DE2826",
    loginBackground: "#FFFFFF",

    // Card surfaces and on-card text.
    cardPrimary: "#FFFFFF",
    cardSurface: "#FFFFFF",
    upcomingChipBackground: "#CFCFCA",
    cardText: "#8E8E8E",
    cardTitle: "#DE2826",
    cardTextMuted: "#8E8E8E",
    cardTextDim: "#979797",
    cardDivider: "#E0E0E0",

    // Page-level typography.
    textPrimary: "#1C1B1C",
    scheduleSectionMonth: "#1C1B1C",
    textSecondary: "#534341",
    readMoreButtonColor: "#DE2826",

    // Shared accents and filters.
    accentActive: "#DE2826",

    filterActiveBg: "#FCE9E9",
    datePickerBackground: "#F1F1F1",
    selectOptionBackground: "#F1F1F1",
    filterActiveColor: "#DE2826",
    filterInactiveBg: "transparent",
    filterInactiveColor: "#8E8E8E",
    filterInactiveBorder: "1px solid #EEEEEE",
    filterActiveBorder: "none",
    trainerFilterActiveBg: "#FCE9E9",
    trainerFilterActiveColor: "#DE2826",
    trainerFilterActiveHoverBg: "#FCE9E9",
    trainerFilterActiveBorder: "none",
    trainerFilterInactiveBg: "transparent",
    trainerFilterInactiveColor: "#8E8E8E",
    trainerFilterInactiveHoverBg: "transparent",
    trainerFilterInactiveBorder: "1px solid #EEEEEE",

    // Stepper state colors.
    stepperActive: "#DE2826",
    stepperInactive: "#C9C6C5",

    // Page-muted text and brand logo fill.
    pageTextMuted: "#8E8E8E",
    logoFill: "#DE2826",

    // Tabs and date buttons.
    tabInactiveColor: "#000000",
    tabActiveColor: "#DE2826",
    dateButtonSelectedText: "#FFFFFF",

    // Empty-state and sheet/dialog tokens.
    emptyStateIconColor: "#E0E0E0",

    sheetBackground: "#FFFFFF",
    sheetText: "#121212",
    reservationDateRangeIcon: "#8E8E8E",
    dialogText: "#000000",
    dialogTextMuted: "#979797",
    dialogCreditIcon: "#484647",
    fabBackground: "#DE2826",
    fabBackgroundHover: "#DE2826",
    errorLight: "#C74C3F",
  },

  // Shared shadows.
  shadows: {
    card: "0px 1px 3px 0px #0000001A",
    bottomSheet: "0px -12px 40px rgba(0, 0, 0, 0.12)",
    cardSubtle: "0px 1px 2px rgba(0, 0, 0, 0.04)",
    button: "0px 4px 12px rgba(0,0,0,0.25)",
    buttonHover: "0px 6px 16px rgba(0,0,0,0.3)",
    dialog: "0px 12px 32px rgba(0, 0, 0, 0.18)",
    dialogHover: "0px 6px 14px rgba(0, 0, 0, 0.25)",
    videoPlay: "0 8px 16px rgba(0,0,0,0.35)",
  },

  // Shared button system tokens.
  button: {
    surface: "solid",
    backgroundColor: "#DE2826",
    color: "#FFFFFF",
    hoverBackgroundColor: "#FCE9E9",
    hoverColor: "#DE2826",
    disabled: {
      backgroundColor: "rgba(0, 0, 0, 0.12)",
      color: "rgba(0, 0, 0, 0.38)",
    },
    secondary: {
      backgroundColor: "#FFFFFF",
      color: "#1F1F1F",
      borderColor: "#BDBDBD",
      hoverBackgroundColor: "#FFFFFF",
      hoverColor: "#1F1F1F",
      hoverBorderColor: "#BDBDBD",
    },
  },
};
